import { emptyApi } from "store/apis/emptyApi";
import type {
  ExtractionReview,
  FinishReviewResult,
  ReviewFieldAction,
} from "v2/types/extractionReview.types";
import type {
  ApiDocumentTypeGroup,
  ApiOthers,
  ApiOverview,
  ApiProcessingStatus,
  ApiRecentGroup,
  ApiUploadedDocument,
} from "./entityDocumentsTransforms";
import {
  toEntityOverview,
  toOtherDocuments,
  toProcessingStatus,
  toRecentDocuments,
  toRequiredItems,
  toUploadedDocuments,
} from "./entityDocumentsTransforms";

/**
 * Inkle Vault API — entity documents. Base: api/inkle/documents/vault/
 * The shared baseQuery unwraps one `{ data }` envelope, so each transformResponse
 * receives the inner payload.
 */

const VAULT = "api/inkle/documents/vault";

export type EntitySection = "About the company" | "Books & financials" | "Tax";

export const DOCUMENT_TYPE_STATUS = {
  UNAVAILABLE: 1,
  ALREADY_UPLOADED: 2,
} as const;

export type DocumentTypeStatus =
  (typeof DOCUMENT_TYPE_STATUS)[keyof typeof DOCUMENT_TYPE_STATUS];

export type RecentSource = "you" | "inkle";

export type DocumentFile = {
  uuid: string;
  name: string;
  fileType: string;
};

export type RequiredItem = {
  id: string;
  section: EntitySection;
  title: string;
  label: string;
  description: string;
  isUrgent: boolean;
  isSatisfied: boolean;
  status: DocumentTypeStatus | null;
};

export type RecentDocument = {
  id: string;
  section: EntitySection | null;
  name: string;
  meta: string;
  files: DocumentFile[];
};

export type OtherDocument = {
  id: string;
  name: string;
  uploadedBy: string;
  uploadedOn: string;
};

export type AssignableCategory = {
  categoryGroup: number;
  label: string;
};

export type OtherDocumentsResponse = {
  documents: OtherDocument[];
  categories: AssignableCategory[];
};

export type EntityProfileOverview = {
  completeness: number;
  unmatchedCount: number;
};

export type ProcessingStatus = {
  allDone: boolean;
  pendingCount: number;
  totalCount: number;
  matchedDocumentIds: string[];
};

export type UploadedDocumentAnalysis = {
  uuid: string;
  processingStatus: number;
};

export type UploadedDocument = {
  uuid: string;
  name: string;
  fileType: string;
  fileSize: number;
  groupId: string;
  analysis: UploadedDocumentAnalysis | null;
  matches: unknown[];
};

export const POST_UPLOAD_TAGS = [
  "ENTITY_PROFILE_OVERVIEW",
  "ENTITY_RECENT_DOCUMENTS",
  "ENTITY_DOCUMENT_TYPES",
  "ENTITY_EXTRACTION_REVIEW",
] as const;

// Extraction-review types/constants/helpers live in v2/types + v2/constants + v2/utils.

export const entityDocumentsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getEntityDocumentTypes: build.query<
      RequiredItem[],
      { entityId?: string; groupId?: string }
    >({
      query: ({ entityId, groupId }) =>
        `${VAULT}/document-types/?${
          groupId ? `group_id=${groupId}` : `entity_id=${entityId}`
        }`,
      transformResponse: (response: ApiDocumentTypeGroup[]) =>
        toRequiredItems(response),
      providesTags: ["ENTITY_DOCUMENT_TYPES"],
    }),

    getEntityOverview: build.query<EntityProfileOverview, { entityId: string }>(
      {
        query: ({ entityId }) => `${VAULT}/entity/${entityId}/documents/`,
        transformResponse: (response: ApiOverview) =>
          toEntityOverview(response),
        providesTags: ["ENTITY_PROFILE_OVERVIEW"],
      }
    ),

    getRecentDocuments: build.query<
      RecentDocument[],
      { entityId: string; by: RecentSource }
    >({
      query: ({ entityId, by }) =>
        `${VAULT}/entity/${entityId}/recent-documents/?by=${by}`,
      transformResponse: (response: ApiRecentGroup[]) =>
        toRecentDocuments(response),
      providesTags: ["ENTITY_RECENT_DOCUMENTS"],
    }),

    getOtherDocuments: build.query<
      OtherDocumentsResponse,
      { entityId: string; search?: string }
    >({
      query: ({ entityId, search }) =>
        `${VAULT}/entity/${entityId}/documents/?category=others${
          search?.trim() ? `&q=${encodeURIComponent(search.trim())}` : ""
        }`,
      transformResponse: (response: ApiOthers) => toOtherDocuments(response),
      providesTags: ["ENTITY_OTHER_DOCUMENTS"],
    }),

    getProcessingStatus: build.query<
      ProcessingStatus,
      { entityId: string; fileIds: string[] }
    >({
      query: ({ entityId, fileIds }) =>
        `${VAULT}/entity/${entityId}/processing-status/?file_ids=${fileIds.join(
          ","
        )}`,
      transformResponse: (response: ApiProcessingStatus) =>
        toProcessingStatus(response),
    }),

    bulkUploadDocuments: build.mutation<
      UploadedDocument[],
      { entityId: string; files: File[]; documentTypeId?: string }
    >({
      query: ({ entityId, files, documentTypeId }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        if (documentTypeId) {
          formData.append("document_type_id", documentTypeId);
        }
        return {
          url: `${VAULT}/entity/${entityId}/documents/`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (response: ApiUploadedDocument[]) =>
        toUploadedDocuments(response),
      invalidatesTags: (result, error, { documentTypeId }) =>
        documentTypeId ? [...POST_UPLOAD_TAGS] : [],
    }),

    replaceDocument: build.mutation<
      unknown,
      { entityId: string; fileUuid: string; file: File }
    >({
      query: ({ entityId, fileUuid, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${VAULT}/entity/${entityId}/documents/${fileUuid}/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["ENTITY_RECENT_DOCUMENTS", "ENTITY_OTHER_DOCUMENTS"],
    }),

    deleteDocument: build.mutation<
      unknown,
      { entityId: string; fileUuid: string }
    >({
      query: ({ entityId, fileUuid }) => ({
        url: `${VAULT}/entity/${entityId}/documents/${fileUuid}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ENTITY_RECENT_DOCUMENTS",
        "ENTITY_OTHER_DOCUMENTS",
        "ENTITY_PROFILE_OVERVIEW",
        "ENTITY_DOCUMENT_TYPES",
        "ENTITY_EXTRACTION_REVIEW",
      ],
    }),

    classifyDocument: build.mutation<
      unknown,
      {
        entityId: string;
        fileUuid: string;
        documentTypeId?: string;
        categoryGroup?: number;
      }
    >({
      query: ({ entityId, fileUuid, documentTypeId, categoryGroup }) => ({
        url: `${VAULT}/entity/${entityId}/documents/${fileUuid}/`,
        method: "PATCH",
        body: documentTypeId
          ? { document_type_id: documentTypeId }
          : { category_group: categoryGroup },
      }),
      invalidatesTags: [
        "ENTITY_OTHER_DOCUMENTS",
        "ENTITY_RECENT_DOCUMENTS",
        "ENTITY_DOCUMENT_TYPES",
        "ENTITY_PROFILE_OVERVIEW",
      ],
    }),

    getExtractionReview: build.query<
      ExtractionReview | null,
      { entityId: string }
    >({
      query: ({ entityId }) => `${VAULT}/entity/${entityId}/extraction-review/`,
      providesTags: ["ENTITY_EXTRACTION_REVIEW"],
    }),

    patchReviewField: build.mutation<
      ExtractionReview | null,
      {
        entityId: string;
        fieldId: string;
        action: ReviewFieldAction;
        candidateUuid?: string;
      }
    >({
      query: ({ entityId, fieldId, action, candidateUuid }) => ({
        url: `${VAULT}/entity/${entityId}/extraction-review/field/`,
        method: "PATCH",
        body: { field_id: fieldId, action, candidate_uuid: candidateUuid },
      }),
      // No invalidatesTags here: a per-PATCH refetch would race rapid multi-clicks — an
      // in-flight GET returns server state from before the other clicks committed and
      // reverts them in-cache until it re-settles (the "snap back then re-confirm"
      async onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            entityDocumentsApi.util.updateQueryData(
              "getExtractionReview",
              { entityId },
              () => data ?? null
            )
          );
        } catch {}
      },
    }),

    finishReview: build.mutation<FinishReviewResult, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${VAULT}/entity/${entityId}/extraction-review/finish/`,
        method: "POST",
      }),
      invalidatesTags: [
        "ENTITY_EXTRACTION_REVIEW",
        "ENTITY_PROFILE_OVERVIEW",
        "ENTITY_RECENT_DOCUMENTS",
        "TAX_SETUP_ANSWERS",
        "TAX_DETECTED_FILINGS",
      ],
    }),

    setDocumentTypeStatus: build.mutation<
      unknown,
      {
        entityId: string;
        documentTypeId: string;
        status: DocumentTypeStatus | null;
      }
    >({
      query: ({ entityId, documentTypeId, status }) => ({
        url: `${VAULT}/entity/${entityId}/document-types/${documentTypeId}/status/`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["ENTITY_DOCUMENT_TYPES", "ENTITY_PROFILE_OVERVIEW"],
    }),
  }),
});

export const {
  useGetEntityDocumentTypesQuery,
  useGetEntityOverviewQuery,
  useGetRecentDocumentsQuery,
  useGetOtherDocumentsQuery,
  useGetProcessingStatusQuery,
  useBulkUploadDocumentsMutation,
  useReplaceDocumentMutation,
  useDeleteDocumentMutation,
  useClassifyDocumentMutation,
  useGetExtractionReviewQuery,
  usePatchReviewFieldMutation,
  useFinishReviewMutation,
  useSetDocumentTypeStatusMutation,
} = entityDocumentsApi;
